/**
 * WI #8606 — Shared date formatting utility using Intl.DateTimeFormat.
 *
 * Replaces 4 divergent call sites that each produced slightly different
 * month abbreviations via moment + destructive .replace() chains. Those
 * chains corrupted accented characters (e.g. "Août" → "AoûT") and could
 * silently produce incorrect output across different locales.
 *
 * This utility is the single source of truth for the "MMM DD" display
 * format shown in the Quick Search date fields.
 *
 * Supported locales (Hola active locales):
 *   en, en-GB, es, es-ES, de, fr, it, pt-BR
 */
/**
 * Formats a date value into a locale-aware abbreviated "month + day" string.
 * First letter is always capitalized for consistent UI display.
 *
 * @param date  - A Date object, ISO 8601 string, or any value accepted by `new Date()`.
 * @param locale - BCP 47 locale tag (e.g. "en", "es", "fr", "pt-BR", "en-GB").
 *                 Falls back to "en" if omitted or unrecognised.
 * @returns     A string like "Aug 15", "Août 15", "Ago 15" depending
 *              on the locale. Never throws — returns empty string on invalid input.
 */
export function formatDateLocale(date, locale = "en") {
    var _a;
    if (!date)
        return "";
    let d;
    if (date instanceof Date) {
        d = date;
    }
    else {
        // new Date("YYYY-MM-DD") parses as UTC midnight; use local-date parsing to
        // avoid off-by-one in negative UTC-offset time zones.
        if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
            const [year, month, day] = date.split("-").map(Number);
            d = new Date(year, month - 1, day);
        }
        else {
            d = new Date(date);
        }
    }
    if (isNaN(d.getTime()))
        return "";
    try {
        const month = new Intl.DateTimeFormat(locale, { month: "short" }).format(d);
        const day = String(d.getDate()).padStart(2, "0");
        // Strip trailing period that some locales append to month abbreviation (e.g. German "Aug.")
        const cleanMonth = month.replace(/\.$/u, "");
        // Always capitalize first letter for consistent UI display (e.g. fr "août" → "Août")
        const capitalized = cleanMonth.charAt(0).toUpperCase() + cleanMonth.slice(1);
        return `${capitalized} ${day}`;
    }
    catch (_b) {
        // Fallback for environments where Intl.DateTimeFormat is unavailable or
        // the locale string is malformed — return a plain English abbreviation.
        const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = (_a = MONTH_ABBR[d.getMonth()]) !== null && _a !== void 0 ? _a : "";
        const day = String(d.getDate()).padStart(2, "0");
        return `${month} ${day}`;
    }
}
